import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AbstractComponent, FocusableComponent } from '../../abstract.component';
import { SolIcon } from '@sol/icons';
import { BrowserTools } from '../../../tools/browser-tools';
import { SolText } from '../../../models/core/sol-text';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolCheckbox, SolCheckboxValue } from '../../../models/core/sol-checkbox';

@Component({
  selector: 'sol-checkbox',
  imports: [TranslatePipe],
  templateUrl: './sol-checkbox.component.html',
})
export class SolCheckboxComponent extends AbstractComponent<SolCheckbox> {
  // Static references
  protected readonly SolCheckboxValue = SolCheckboxValue;
  protected readonly SolIcon = SolIcon;

  // Computed
  protected readonly disabled$ = this.compute(state => state.disabled);
  protected readonly value$ = this.compute(state => state.value);
  protected readonly text$ = this.compute(state => state.text);
  protected readonly info$ = this.compute(state => state.info);
  protected readonly custom$ = this.compute(state => state.custom);
  protected readonly selected$ = this.compute(state => state.selected);

  // Events
  @Output('selected')
  public selectedEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output('value')
  public valueEmitter: EventEmitter<SolCheckboxValue> = new EventEmitter<SolCheckboxValue>();
  @Output('focused')
  public focusedEmitter = new EventEmitter<boolean>();

  // Binding
  @ViewChild('inputRef') private inputRef!: ElementRef<HTMLInputElement>;

  constructor() {
    super(SolCheckbox.of());

    this.watchComputed(
      state => state.focused,
      focused => this.focusedEmitter.emit(focused),
    );
  }

  @Input()
  public set checkbox(checkbox: SolCheckbox) {
    this.patch(checkbox);
  }

  @Input()
  public set disabled(disabled: boolean) {
    this.update(state => state.withDisabled(disabled));
  }

  @Input()
  public set selected(selected: boolean) {
    this.update(state => state.withSelected(selected));
  }

  @Input()
  public set value(value: SolCheckboxValue) {
    this.update(state => state.withValue(value));
  }

  @Input()
  public set text(text: string) {
    this.update(state => state.withText(text));
  }

  @Input()
  public set info(info: SolText | undefined) {
    this.update(state => state.withInfo(info));
  }

  @Input()
  public set custom(custom: boolean) {
    this.update(state => state.withCustom(custom));
  }

  public onClick(event?: any) {
    if (this.state.disabled) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();
    this.update(state => state.withToggle());
    this.selectedEmitter.emit(this.state.selected);
    this.valueEmitter.emit(this.state.value);
  }

  protected onFocused(focused: boolean) {
    this.update(state => state.withFocused(focused));
  }

  protected override focusable(): FocusableComponent | undefined {
    return BrowserTools.toFocusable(this.inputRef);
  }
}
